import { Link } from 'react-router-dom';
import { DocSection, kbd } from '../../pages/shell';
import { DocShot } from '../DocShot';

export const toc = [
  { id: 'report-bug', label: 'Report a bug in-app' },
  { id: 'find-logs', label: 'Find your logs' },
  { id: 'request-feature', label: 'Request a feature' },
  { id: 'discord', label: 'Join the Discord' },
];

export default function Body() {
  return (
    <>
      <DocSection id="report-bug" title="Report a bug in-app">
        <p>
          The fastest way to get a bug looked at is <span style={kbd}>Help → Report a bug</span>
          {' '}inside ClipRadar — it reaches us directly, and your footage is never part of it.
        </p>
        <DocShot
          slug="get-help"
          shot="help-panel"
          alt="The Help panel, listing Report a bug, docs, Discord, and feature requests"
          caption="Help is one click away from any tab."
        />
        <p>
          The dialog asks <span style={kbd}>What happened?</span> and an optional email, plus an{' '}
          <span style={kbd}>Include diagnostics</span> toggle. Turning diagnostics on attaches your
          app version, OS, GPU, and a handful of recent log lines — scrubbed of usernames and file
          paths before they ever leave your machine, and never your footage or file contents.
        </p>
        <DocShot slug="get-help" shot="report-dialog" alt="The Report-a-bug dialog" />
        <p>
          Click <span style={kbd}>Send report</span> and ClipRadar confirms it went out.
        </p>
        <DocShot
          slug="get-help"
          shot="thanks-toast"
          alt='The "Thanks — your report is on its way" confirmation toast'
        />
        <p>
          ClipRadar sometimes offers this same dialog on its own — after a run of empty searches, a
          failed analysis, or an Ultra Search fallback — so you don't have to go looking for it.
          Turn those prompts off from{' '}
          <Link to="/docs/interface/settings#privacy">Settings → Privacy &amp; diagnostics</Link>{' '}
          if you'd rather not see them. For the exact boundary on what does and doesn't leave your
          machine, see{' '}
          <Link to="/docs/concepts/local-and-private">Local &amp; private</Link>.
        </p>
      </DocSection>

      <DocSection id="find-logs" title="Find your logs">
        <p>
          Most of the time you shouldn't need to touch a log file directly — checking{' '}
          <span style={kbd}>Include diagnostics</span> on a bug report scrubs and attaches the
          relevant lines automatically, before anything leaves your machine. If you're asked for
          the raw files, ClipRadar writes two: a backend log, which is scrubbed of usernames and
          paths as it's written, and an app log, which isn't scrubbed the same way — so skim it
          before sharing it anywhere other than the in-app report. Both rotate so they don't grow
          forever.
        </p>
        <p>
          Open File Explorer and go to <span style={kbd}>%APPDATA%\ClipRadar\logs</span> to see
          them. Full detail on where every ClipRadar file lives is on{' '}
          <Link to="/docs/reference/where-your-data-lives#logs">Where your data lives</Link>.
        </p>
      </DocSection>

      <DocSection id="request-feature" title="Request a feature">
        <p>
          Feature requests go on ClipRadar's public roadmap board, not into a black hole. Open{' '}
          <span style={kbd}>Help → Feature Requests</span> to post an idea or upvote one that's
          already there.
        </p>
      </DocSection>

      <DocSection id="discord" title="Join the Discord">
        <p>
          When the docs and the in-app report don't get you unstuck, Discord is the fastest way to
          reach an actual person. Open <span style={kbd}>Help → Join our Discord</span> from
          inside ClipRadar to get the invite.
        </p>
      </DocSection>
    </>
  );
}
